import * as React from 'react'

/**
 * Full-viewport startup gate. The shell holds first paint behind this while the
 * startup handshake is in flight — `whoami`, the domain configuration and, for a
 * signed-in visitor, device registration — and only lifts it once {@link useAuth}'s
 * `ready` flips true. That is the "call whoami and the domain configuration first"
 * contract made visible: no header, pilet page or route guard mounts until they
 * have answered.
 *
 * Deliberately quiet. It paints the app's own background immediately (so the gate
 * reads as the app starting, not as a separate screen) and only fades the indicator
 * in after a short delay — a handshake that settles in 200ms should look like a
 * page that simply appeared, not like a spinner that flashed. The delay lives in
 * CSS (`animation-delay`) rather than a timer, so there is no state, no re-render
 * and nothing to clean up.
 *
 * `role="status"` + `aria-live="polite"` announce the loading state to assistive
 * tech; the bar is decorative (`aria-hidden`) and the label carries the text.
 */
const LoadingScreen: React.FC = () => (
  <div className='LoadingScreen' role='status' aria-live='polite'>
    <div className='LoadingScreen__indicator'>
      {/* An indeterminate sweep rather than a spinner: it has no implied progress
          to be wrong about, and a slow horizontal ease reads calmer than a
          rotation at any speed. */}
      <div className='LoadingScreen__bar' aria-hidden='true' />
      <p className='LoadingScreen__label'>Loading…</p>
    </div>
  </div>
)

export default LoadingScreen
